
    import __i18nConfig from '@next-translate-root/i18n.js'
    import __appWithI18n from 'next-translate/appWithI18n'
    /* eslint-disable react/prop-types */
import * as yup from "yup";
import { useMemo, useEffect, Suspense, useRef } from "react";
import type { AppProps } from "next/app";
import { useRouter } from "next/router";
import Script from "next/script";
import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";
import useTranslation from "next-translate/useTranslation";
import { enUS as DataGridEn, trTR as DataGridTr } from "@mui/x-data-grid";
import { enUS as CoreEn, trTR as CoreTr } from "@mui/material/locale";
import "@/utils/helpers/prototypes";
import { ToastContainer } from "react-toastify";
import { LocaleValidations } from "@/utils/validations/localeValidations";
import { theme } from "@/styles/createTheme";
import { DynamicLocalizationContext } from "@/context";
import { SMSVerificationDialog } from "@/components/common/sms-verification-dialog";
import { usePhoneConfirmStore } from "@/store/phone-confirm";
import "@/styles/global.scss";
import "@/utils/validations/extendsions";
import "react-toastify/dist/ReactToastify.css";
import { Footer } from "@/components/common";
import { Box } from "@/components/core/box";
function NextApp({ Component, pageProps }: AppProps) {
    const { t, lang } = useTranslation();
    const router = useRouter();
    yup.setLocale(LocaleValidations(t));
    const languages = {
        tr: {
            ...CoreTr,
            ...DataGridTr,
        },
        en: {
            ...CoreEn,
            ...DataGridEn,
        },
    };
    const themeWithLocale = useMemo(() => createTheme(theme, languages[lang as keyof typeof languages]), [lang]);
    const providerValues = useMemo(() => ({ lang }), [lang]);
    const open = usePhoneConfirmStore((state) => state.open);
    const setOpen = usePhoneConfirmStore((state) => state.setOpen);
    const onSubmit = usePhoneConfirmStore((state) => state.onSubmit);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        const handleZoom = (event: Event) => {
            const wheelEvent = event as WheelEvent;
            if (wheelEvent.deltaY && wheelEvent.ctrlKey) {
                event.preventDefault();
            }
        };
        const handleMouseMove = (event: Event) => {
            event.preventDefault();
            resetTimeout();
        };
        const handleWheel = (event: Event) => {
            const wheelEvent = event as WheelEvent;
            if (wheelEvent.ctrlKey) {
                event.preventDefault();
            }
            resetTimeout();
        };
        function resetTimeout() {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(logout, 5 * 60 * 1000);
        }
        const handleResize = () => {
            window.innerWidth;
            window.innerHeight;
        };
        function logout() {
            if (window.location.pathname !== "/login") {
                alert("Oturumunuz Hareketsizlik Nedeniyle Sonlandırılmıştır.");
                window.location.href = "/login";
            }
        }
        window.addEventListener("wheel", handleWheel, { passive: false });
        window.addEventListener("mousemove", handleMouseMove); // Fare hareketini dinle
        window.addEventListener("mousewheel", handleWheel, { passive: false });
        window.addEventListener("DOMMouseScroll", handleMouseMove, { passive: false });
        window.addEventListener("gesturechange", handleZoom, { passive: false });
        window.addEventListener("resize", handleResize, { passive: false });
        handleResize();
        resetTimeout();
        return () => {
            window.removeEventListener("wheel", handleWheel);
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mousewheel", handleWheel);
            window.removeEventListener("DOMMouseScroll", handleWheel);
            window.removeEventListener("gesturechange", handleZoom);
            window.removeEventListener("resize", handleResize);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);
    useEffect(() => {
        const handleRouteChange = (url: string) => {
            window.gtag?.("config", "G-SZ3ED04Y1Z", {
                page_path: url,
            });
        };
        router.events.on("routeChangeComplete", handleRouteChange);
        return () => {
            router.events.off("routeChangeComplete", handleRouteChange);
        };
    }, [router.events]);
    return (<>
      <Script src="https://www.googletagmanager.com/gtag/js?id=G-SZ3ED04Y1Z" strategy="afterInteractive"/>
      <Script id="google-analytics" strategy="afterInteractive">
        {`
         window.dataLayer = window.dataLayer || [];
         function gtag(){dataLayer.push(arguments);}
         window.gtag = gtag;
         gtag('js', new Date());
         gtag('config', 'G-SZ3ED04Y1Z', {
           anonymize_ip: true
         });
       `}
      </Script>

      <DynamicLocalizationContext.Provider value={providerValues}>
        <ThemeProvider theme={themeWithLocale}>
          <ToastContainer position="top-right"/>
          <SMSVerificationDialog open={open} onClose={() => setOpen(false)} onSubmit={onSubmit}/>
          <CssBaseline />
          <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
            <Box component="main" sx={{ flex: 1 }}>
              <Suspense fallback={<div>Loading...</div>}>
                <Component {...pageProps}/>
              </Suspense>
            </Box>
            <Footer />
          </Box>
        </ThemeProvider>
      </DynamicLocalizationContext.Provider>
    </>);
}

    export default __appWithI18n(NextApp, {
      ...__i18nConfig,
      isLoader: true,
      skipInitialProps: true,
      loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({})),
    });
  